import { useCallback, useMemo } from 'react';

import { TargetModel } from '../../../modules/Apps';
import { useUpdateTarget } from '../../../modules/Apps/Targets';
import { PopoverStateReturn } from '../../common/Drawer/Drawer.context';
import { FormDrawer } from '../../common/FormDrawer';
import { useToasterContext } from '../../common/Toaster';

import { createTargetsFormValidationSchema, TargetsForm } from './TargetsForm';
import { TargetsFormValues, TargetsFormValuesToDto } from './types';

interface EditTargetsDrawerProps {
  state: PopoverStateReturn;
  target: TargetModel;
}

export function EditTargetsDrawer({ state, target }: EditTargetsDrawerProps) {
  const { mutateAsync } = useUpdateTarget();
  const { showSuccess, showError } = useToasterContext();

  const handleSubmit = useCallback(
    async (values: TargetsFormValues) => {
      await mutateAsync(TargetsFormValuesToDto(values, target.targetId), {
        onSuccess: () => showSuccess('Target successfully created.'),
        onError: () =>
          showError('There was an error creating the target. Please, try again later.'),
      });
    },
    [mutateAsync, showError, showSuccess, target.targetId],
  );

  const {
    appId,
    forwardMethod,
    forwardUrl,
    name,
    status,
    messageActionType,
    message,
    scopes = [],
    authHeaders = [],
  } = target;

  const initialValues: TargetsFormValues = useMemo(
    () => ({
      appId,
      authHeaders,
      status,
      forwardMethod,
      forwardUrl,
      name,
      scopes,
      messageActionType,
      message,
    }),
    [
      appId,
      authHeaders,
      status,
      forwardMethod,
      forwardUrl,
      message,
      messageActionType,
      name,
      scopes,
    ],
  );

  const validationSchema = useMemo(() => createTargetsFormValidationSchema(), []);

  return (
    <FormDrawer
      state={state}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      title="Edit App Target"
      subtitle="Edit target used to forward inbound SMS messages to another application"
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {() => <TargetsForm appId={appId} />}
    </FormDrawer>
  );
}
